/**
 * Tema da PWA.
 *
 * Usa exatamente o mecanismo do protótipo aprovado: `data-theme` no elemento
 * raiz, com as cores em CSS custom properties. A regra de resolução vem de
 * `@nv/tokens` — a mesma que o cliente React Native usa.
 */

import { colors, resolveThemeName, type ThemeName, type ThemePreference } from '@nv/tokens';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const PREFERENCE_KEY = 'nv.theme';

interface ThemeContextValue {
  name: ThemeName;
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(PREFERENCE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Safari em navegação privada pode lançar ao ler storage. Seguir com o
    // padrão é melhor do que quebrar a inicialização do app.
  }
  // O app abre claro na primeira vez (decisão do PO, teste físico item 1):
  // quem preferir escuro ou "sistema" troca no Perfil, e a escolha fica.
  return 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [systemScheme, setSystemScheme] = useState<'light' | 'dark' | null>(null);

  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: light)');
    const apply = () => setSystemScheme(query.matches ? 'light' : 'dark');
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, []);

  const name = resolveThemeName(preference, systemScheme);

  useEffect(() => {
    document.documentElement.dataset.theme = name;
    // A cor da barra do navegador acompanha o tema — sem isto, uma faixa
    // clara aparece acima do app escuro no iOS.
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', colors[name].bg);
    // `color-scheme` diz ao navegador e ao Android que fundo a barra de
    // status e os controles nativos devem assumir. Sem isto, no tema claro
    // a barra ficava preta com ícones pretos (teste físico, item 14).
    document.documentElement.style.colorScheme = name;
    document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', name);
  }, [name]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(PREFERENCE_KEY, next);
    } catch {
      // Preferência não persistida é degradação aceitável.
    }
  }, []);

  const value = useMemo(() => ({ name, preference, setPreference }), [
    name,
    preference,
    setPreference,
  ]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme precisa estar dentro de <ThemeProvider>.');
  return context;
}

/**
 * Só o nome do tema, sem exigir o provider: para peças visuais (ilustração)
 * que também aparecem em testes de tela montados sem ele. Fora do provider,
 * claro — a mesma reserva de `resolveThemeName`.
 */
export function useNomeDoTema(): ThemeName {
  return useContext(ThemeContext)?.name ?? 'light';
}
